import { memo, useCallback } from 'react';
import { Handle, type Node, type NodeProps } from '@xyflow/react';
import { makeStyles, mergeClasses, tokens, shorthands, Tooltip } from '@coreai/fluentui-react';
import { FlashRegular } from '@fluentui/react-icons';
import { useHandleLayout } from './useHandlePositions';
import { useDetailLevel } from './FloatingNode';

export type PlaceholderTriggerNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  /** Callback when the placeholder is clicked to add a trigger */
  onClick?: () => void;
};

export type PlaceholderTriggerNodeType = Node<PlaceholderTriggerNodeData, 'placeholderTrigger'>;

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalSNudge,
    cursor: 'pointer',
    position: 'relative',
    width: '40px',
    overflow: 'visible',
  },
  iconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'all',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      boxShadow: tokens.shadow8,
    },
  },
  focusVisible: {
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (forced-colors: active)': {
      ':focus-visible': {
        outline: '3px solid CanvasText',
        outlineOffset: '2px',
      },
    },
  },
  icon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '1.2',
    fontStyle: 'italic',
  },
});

/**
 * Placeholder node shown when there are actions but no trigger.
 * Indicates that a trigger needs to be added to start the workflow.
 * Clicking opens the operation picker in trigger mode.
 */
export const PlaceholderTriggerNode = memo(({ data }: NodeProps<PlaceholderTriggerNodeType>) => {
  const styles = useStyles();
  const handles = useHandleLayout(40);
  const detailLevel = useDetailLevel();
  const isIconOnly = detailLevel === 'icon';

  const handleClick = useCallback(() => {
    data.onClick?.();
  }, [data]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      data.onClick?.();
    }
  }, [data]);

  const content = (
    <div
      className={mergeClasses(styles.wrapper, styles.focusVisible)}
      role="button"
      tabIndex={0}
      aria-label="Add a trigger to start the workflow"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.iconBox}>
        <div className={styles.icon}>
          <FlashRegular />
        </div>
      </div>
      {!isIconOnly && (
        <div className={styles.label} title="Add a trigger">
          Add trigger
        </div>
      )}
      <Handle
        type="source"
        position={handles.output.position}
        id="output"
        isConnectable={false}
        style={handles.output.style}
      />
    </div>
  );

  if (isIconOnly) {
    return (
      <Tooltip content="Add a trigger" relationship="description" positioning="below">
        {content}
      </Tooltip>
    );
  }

  return content;
});

PlaceholderTriggerNode.displayName = 'PlaceholderTriggerNode';
